import type { AxiosError } from "axios";

/** Give up after this many attempts rather than hammering a struggling backend. */
const RETRY_LIMIT = 3;
const RETRY_BASE_MS = 1000;

/**
 * Retry a failed request only when retrying can change the answer.
 *
 * A 4xx is the backend's verdict on the request itself, a plan the user cannot
 * access, an out-of-stock configuration, a missing object, and repeating it only
 * produces the same refusal. A 5xx or a dropped connection is the opposite: the
 * request was fine and the backend was not, which is exactly what a retry is for.
 *
 * It is the dashboard's default (`AppProviders`). SWR's own policy retries every
 * error, 4xx included, with no limit, so one request the backend refuses kept
 * being asked for as long as the page stayed open (R-2207).
 */
export function retryServerErrorsOnly(
  error: AxiosError,
  _key: unknown,
  _config: unknown,
  revalidate: (options: { retryCount: number }) => void,
  { retryCount }: { retryCount: number }
): void {
  const status = error?.response?.status;
  if (status !== undefined && status < 500) {
    return;
  }
  if (retryCount > RETRY_LIMIT) {
    return;
  }
  setTimeout(
    () => revalidate({ retryCount }),
    RETRY_BASE_MS * 2 ** (retryCount - 1)
  );
}
